import { Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { ListingType } from 'app/types/listing';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { Pressable, View } from 'react-native';
const theme = createDSTheme();

export type AllListingsTab = ListingType | 'people';

interface TabSelectorProps {
  currentTab: AllListingsTab;
  onTabChange: (tab: AllListingsTab) => void;
  offersTotal: number;
  requestsTotal: number;
  peopleTotal: number;
  showPeopleTab?: boolean;
}

interface TabButtonProps {
  tabType: AllListingsTab;
  currentTab: AllListingsTab;
  label: string;
  count?: number;
  onPress: () => void;
  prefix?: string;
}

function TabButton({ tabType, currentTab, label, count, onPress, prefix }: TabButtonProps) {
  const isActive = currentTab === tabType;
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityState={{ selected: isActive }}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={onPress}
      style={({ pressed }) => ({
        minHeight: 34,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: theme.borderRadii['radius.full'],
        backgroundColor: isActive
          ? theme.colors['text.primary']
          : isHovered
            ? theme.colors['surface.secondary-neutral']
            : 'transparent',
        opacity: pressed ? 0.82 : 1,
      })}
    >
      <Row alignItems="center" gap="spacing.general.x1">
        {prefix ? (
          <Text fontSize={15} lineHeight={18}>
            {prefix}
          </Text>
        ) : null}
        <Text
          variant="bodyBase"
          color={isActive ? 'white' : isHovered ? 'text.primary' : 'text.secondary'}
          fontSize={14}
          lineHeight={18}
          fontWeight={isActive ? '700' : '600'}
        >
          {label}
        </Text>
        {typeof count === 'number' ? (
          <View
            style={{
              minWidth: 22,
              height: 20,
              paddingHorizontal: 7,
              borderRadius: theme.borderRadii['radius.full'],
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isActive ? 'rgba(255,255,255,0.16)' : theme.colors.white,
              borderWidth: isActive ? 0 : 1,
              borderColor: theme.colors['border.secondary'],
            }}
          >
            <Text color={isActive ? 'white' : 'text.secondary'} fontSize={12} fontWeight="700" lineHeight={14}>
              {count}
            </Text>
          </View>
        ) : null}
      </Row>
    </Pressable>
  );
}

export const TabSelector = ({
  currentTab,
  onTabChange,
  offersTotal,
  requestsTotal,
  peopleTotal,
  showPeopleTab = false,
}: TabSelectorProps) => {
  const { t } = useTranslation('all-listings');

  return (
    <Row
      backgroundColor="surface.tertiary-neutral"
      borderRadius="radius.full"
      gap="spacing.general.x0,5"
      alignItems="center"
      style={{
        borderWidth: 1,
        borderColor: theme.colors['border.secondary'],
      }}
    >
      <TabButton
        count={offersTotal}
        currentTab={currentTab}
        label={t('tabs.offer')}
        onPress={() => onTabChange(ListingType.offer)}
        prefix="🫴"
        tabType={ListingType.offer}
      />

      <TabButton
        count={requestsTotal}
        currentTab={currentTab}
        label={t('tabs.request')}
        onPress={() => onTabChange(ListingType.request)}
        prefix="👋"
        tabType={ListingType.request}
      />

      {showPeopleTab ? (
        <TabButton
          count={peopleTotal}
          currentTab={currentTab}
          label={t('tabs.people')}
          onPress={() => onTabChange('people')}
          tabType="people"
          prefix="👥"
        />
      ) : null}
    </Row>
  );
};
